import { inject, Injectable } from '@angular/core';
import { LoggableAPI } from '../models/protocols/loggable-api';
import { ApiClient } from './api-client';
import { InternalUser } from '../models/users/dto/internal-user';
import { ODataQueryOptions } from '../models/shared/odata-query-options';
import { Endpoints } from './endpoints';
import { ODataResponse } from '../models/protocols/odata-response';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CustomError } from '../models/shared/custom-error';
import { HttpResponse } from '@angular/common/http';
import { ChangeEmailRequest } from '../models/users/requests/change-email-request';
import { CreateInternalUserRequest } from '../models/users/requests/create-internal-user-request';
import { Role } from '../models/roles/role';
import { IpUserSubmissionPermissionResponse } from '../models/users/dto/submission-permissions/ip-user-submission-permission-response';

@Injectable({
  providedIn: 'root'
})
export class InternalUsersAPI implements LoggableAPI {
  constructor() {}

  private apiClient = inject(ApiClient);

  public serviceName = 'InternalUsers';

  public getAllInternalUsers(oDataQueryOptions: ODataQueryOptions): Observable<ODataResponse<InternalUser>> {
    const url = Endpoints.getInternalUsers();
    oDataQueryOptions.setExpand('Roles', 'ProfileBadgeConfiguration');
    return this.apiClient.getOdata<InternalUser>(url, InternalUser, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getInternalUserById(id: number): Observable<InternalUser> {
    const url = Endpoints.getInternalUsersById(id);
    const oDataQueryOptions = new ODataQueryOptions();
    oDataQueryOptions.setExpand('Roles, SubmissionPermissions');
    return this.apiClient.getOdataObj<InternalUser>(url, InternalUser, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public updateInternalUser(internalUser: InternalUser): Observable<InternalUser> {
    const url = Endpoints.updateInternalUser(internalUser?.id.toString(10));
    return this.apiClient.putObj(InternalUser, url, internalUser).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // This endpoint works to both force reset another Internal User's password,
  // and resend the welcome email to an Internal User.
  public forceResetInternalUserPassword(internalUser: InternalUser): Observable<HttpResponse<any>> {
    const url = Endpoints.forceResetInternalUserPassword(internalUser?.id.toString(10));
    return this.apiClient.simpleBodylessPost(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public forceResetInternalUserEmail(req: ChangeEmailRequest, userId: string): Observable<InternalUser> {
    const url = Endpoints.forceResetInternalUserEmail(userId);
    return this.apiClient.postObj(InternalUser, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createInternalUser(req: CreateInternalUserRequest): Observable<InternalUser> {
    const url = Endpoints.createInternalUser();
    return this.apiClient.postObj(InternalUser, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getInternalUsersRoles(
    odataQueryOptions: ODataQueryOptions = new ODataQueryOptions()
  ): Observable<ODataResponse<Role>> {
    const url = Endpoints.getInternalRoles();
    const permissionHeaders = { permissions: [1026] };
    return this.apiClient.getOdata(url, Role, odataQueryOptions, permissionHeaders).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getInternalUserSubmissionPermissions(userId: number): Observable<IpUserSubmissionPermissionResponse> {
    const url = Endpoints.getInternalUserSubmissionPermissions(userId);
    return this.apiClient.getObj(IpUserSubmissionPermissionResponse, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }
}
